import React, { useState, useEffect, useMemo } from 'react'
import {
  Stack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Loading,
  EmptyBox,
  Alert,
  ParticipantMeetingEditor,
  ParticipantMeetingCard,
  WherebyMeeting
} from '@/components/lib'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'

export default function Meetings ({ trialId, participantId, participant }) {
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [editingMeeting, setEditingMeeting] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [selectedMeeting, setSelectedMeeting] = useState(null)
  const {
    user,
    trial,
    team,
    participantMeetings,
    setParticipantMeetingsForParticipant,
    upsertParticipantMeeting,
    deleteParticipantMeeting
  } = useStore()

  const meetings = useMemo(
    () => participantMeetings?.filter((m) => String(m.participant) === String(participantId)) ?? [],
    [participantMeetings, participantId]
  )

  function fetchMeetings () {
    if (!trialId || !participantId) return
    setLoading(true)
    api.participants.listMeetings(trialId, participantId, (d) => {
      setParticipantMeetingsForParticipant(participantId, d.meetings || [])
      setLoading(false)
    }, () => {
      setParticipantMeetingsForParticipant(participantId, [])
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchMeetings()
  }, [trialId, participantId])

  function openCreateModal () {
    setEditingMeeting(null)
    setCreateOpen(true)
  }

  function openEditModal (meeting) {
    setEditingMeeting(meeting)
    setCreateOpen(true)
  }

  function closeModal () {
    setCreateOpen(false)
    setEditingMeeting(null)
  }

  function onMeetingSaved (meeting) {
    upsertParticipantMeeting(meeting)
  }

  function handleDelete (meetingId) {
    setDeletingId(meetingId)
    api.participants.deleteMeeting(trialId, participantId, meetingId, () => {
      deleteParticipantMeeting(meetingId)
      setDeletingId(null)
      utils.toast('success', 'Meeting deleted')
    }, (err) => {
      setDeletingId(null)
      utils.toast('error', err.message)
    })
  }

  if (loading) return <Loading />

  return (
    <Stack gap={5}>

      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Heading>Video call meetings</Heading>
        {utils.canCreateParticipantMeetings(user, trial) && utils.hasFeature(team, 'videoCalls') &&
          <Button size='sm' colorPalette='primary' onClick={openCreateModal}>
            New meeting
          </Button>}
      </Stack>

      {!utils.hasFeature(team, 'videoCalls') &&
        <Alert status='info' title='Video calls are not available' textAlign='left'>
          <Text>Video calls are not available for your team. Please contact support to enable it.</Text>
        </Alert>}

      <WherebyMeeting
        meeting={selectedMeeting}
        onClose={() => setSelectedMeeting(null)}
        participantDisplayId={participant?.id}
      />

      {meetings.length === 0 && (
        <EmptyBox icon='videocall' title='No meetings yet'>
          <Text>Scheduled video calls will appear here once set-up.</Text>
        </EmptyBox>
      )}

      {meetings.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          {meetings.map((meeting) => (
            <ParticipantMeetingCard
              key={meeting._id}
              meeting={meeting}
              trialId={trialId}
              participantId={participantId}
              onJoinHost={setSelectedMeeting}
              onEdit={openEditModal}
              onDelete={handleDelete}
              deletingId={deletingId}
            />
          ))}
        </SimpleGrid>
      )}

      <ParticipantMeetingEditor
        isOpen={createOpen}
        onClose={closeModal}
        trialId={trialId}
        participantId={participantId}
        participant={participant}
        meeting={editingMeeting}
        onSaved={onMeetingSaved}
      />
    </Stack>
  )
}
